import { useState } from 'react';
import { Phone, MessageCircle, CalendarDays } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import { Avatar, AvatarImage, AvatarFallback } from '@rocco/components/avatar';
import { getNameInitials } from '~/modules/user/util/get-name-initials';
import { ClientDirectorBookingModal } from '~/modules-rocco/booking';
import { detailCta, LogoBadgeOverlay } from '~/ui-rocco/pages/detail-shared';

export interface SpecificationRep {
    id: number;
    /** Display name, e.g. "Sonia Loremipsum". */
    name: string;
    /** Position shown under the name, e.g. the member's job title. */
    role: string;
    /** Plain-text bio (Summary with HTML stripped); may be empty. */
    bio: string;
    /** Tel number, null when the member has none. */
    phone: string | null;
    /** Headshot URL; falls back to initials when absent. */
    image?: string;
}

export interface SpecificationRepresentativeRowContentProps {
    reps: SpecificationRep[];
    /** Supplier brand/cover banner shown on the left (Professional banner). */
    brandImage?: { src: string; alt: string };
    /** Supplier logo badge overlaid on the brand image (Professional logo). */
    brandLogo?: { src: string; background?: string | null; alt?: string };
    /** Professional profile link — makes the logo badge clickable. */
    professionalLink?: string;
    /** Opens the product enquiry modal — the CTA when no visible phone exists. */
    onSendMessage?: () => void;
    /**
     * Bookable Client Director session ID. When set, the rep card offers a
     * Book a Meeting CTA that opens the shared booking modal; when absent the
     * card renders its contact CTAs only, with no reserved slot.
     */
    bookingSessionId?: number;
}

/** First name only, for the "Call {name}" CTA label. */
const firstName = (name: string): string => name.trim().split(/\s+/)[0] ?? name;

/** Strip formatting (brackets/dashes/dots/spaces) to a dialable tel: value. */
const toTelHref = (phone: string): string =>
    `tel:${phone.replace(/[^\d+]/g, '')}`;

/**
 * (NN) Specification Representative — per Figma 2354:59152. Supplier brand image
 * (with logo badge) on the left; on the right the professional's team members
 * flagged as specification reps render as selectable pills under a "Sales
 * Representatives" label, with the selected rep's headshot, name, position, bio
 * and a "Call {name}" tel: CTA. When a bookable Client Director session is
 * passed in, a Book a Meeting CTA joins the card and opens the shared booking
 * modal. Closed by default.
 */
export const SpecificationRepresentativeRowContent = ({
    reps,
    brandImage,
    brandLogo,
    professionalLink,
    onSendMessage,
    bookingSessionId,
}: SpecificationRepresentativeRowContentProps) => {
    const [selectedId, setSelectedId] = useState(reps[0]?.id);
    const [bookingOpen, setBookingOpen] = useState(false);
    const selected = reps.find((rep) => rep.id === selectedId) ?? reps[0];

    if (!selected) return null;

    // One filled pill per card: Book a Meeting fills when it sits under the
    // outlined Call CTA (or stands alone), and goes outlined under the filled
    // Send Message CTA.
    const showSendMessage = !selected.phone && Boolean(onSendMessage);
    const bookMeetingVariant = showSendMessage
        ? 'outlined-light'
        : 'filled-light';

    return (
        <div className="pb-[1.8rem] max-md:pb-4">
            <div className="flex gap-[3rem] max-md:flex-col max-md:gap-6">
                {brandImage ? (
                    <div className="relative w-[37.125rem] shrink-0 max-md:w-full">
                        <div className="bg-neutral-200 relative aspect-[594/399] w-full overflow-hidden rounded-tile">
                            <img
                                src={brandImage.src}
                                alt={brandImage.alt}
                                loading="lazy"
                                className="absolute inset-0 size-full object-cover"
                            />
                        </div>
                        {brandLogo ? (
                            <LogoBadgeOverlay
                                logo={brandLogo}
                                to={professionalLink}
                                className="top-[1.5rem] left-[1.5rem]"
                                sizeClassName="size-[2.75rem] md:size-[5.19rem]"
                            />
                        ) : null}
                    </div>
                ) : null}

                <div className="flex min-w-0 flex-1 gap-[3rem] max-md:flex-col max-md:gap-6">
                    {/* Label + pills + bio */}
                    <div className="flex min-w-0 flex-1 flex-col gap-[1.5rem]">
                        <p className="m-0 font-sans text-[0.9375rem] leading-tight font-medium tracking-[-0.05em] text-[#767676]">
                            {reps.length === 1
                                ? 'Sales Representative'
                                : 'Sales Representatives'}
                        </p>
                        <div className="flex flex-wrap items-center gap-[0.375rem]">
                            {reps.map((rep) => {
                                const isSelected = rep.id === selected.id;
                                return (
                                    <button
                                        key={rep.id}
                                        type="button"
                                        onClick={() => setSelectedId(rep.id)}
                                        aria-pressed={isSelected}
                                        className={cn(
                                            'inline-flex h-[1.875rem] items-center justify-center rounded-full px-[0.875rem] font-sans text-[0.9375rem] leading-tight tracking-[-0.01em] capitalize transition-colors',
                                            isSelected
                                                ? 'bg-[#d7d7d7] text-black'
                                                : 'border border-black text-[#181818] hover:bg-black/5',
                                        )}
                                    >
                                        {rep.name}
                                    </button>
                                );
                            })}
                        </div>
                        {selected.bio ? (
                            <p className="m-0 font-sans text-[1.375rem] leading-snug tracking-[-0.02em] text-[#181818]">
                                {selected.bio}
                            </p>
                        ) : null}
                    </div>

                    {/* Selected rep card */}
                    <div className="flex w-[16.5rem] shrink-0 flex-col items-end text-right max-md:w-full max-md:items-start max-md:text-left">
                        <div className="relative size-[5.21rem]">
                            <Avatar className="size-full">
                                {selected.image ? (
                                    <AvatarImage
                                        src={selected.image}
                                        alt={selected.name}
                                    />
                                ) : null}
                                <AvatarFallback className="bg-neutral-200 text-[1.375rem] text-[#707070]">
                                    {getNameInitials(selected.name, 2)}
                                </AvatarFallback>
                            </Avatar>
                            <span className="absolute right-[0.4rem] bottom-[0.4rem] size-[0.585rem] rounded-full border-2 border-white bg-[#5fc88f]" />
                        </div>
                        <p className="m-0 mt-[1.5rem] font-sans text-[1.375rem] leading-snug font-medium text-[#1f1f21]">
                            {selected.name}
                        </p>
                        {selected.role ? (
                            <p className="m-0 mt-[0.5rem] font-sans text-[1.0625rem] leading-snug text-[#1f1f21]">
                                {selected.role}
                            </p>
                        ) : null}
                        {selected.phone ? (
                            <a
                                href={toTelHref(selected.phone)}
                                className={detailCta(
                                    'outlined-light',
                                    'mt-[1.5rem]',
                                )}
                            >
                                Call {firstName(selected.name)}
                                <Phone className="size-4" aria-hidden />
                            </a>
                        ) : onSendMessage ? (
                            <button
                                type="button"
                                onClick={onSendMessage}
                                className={detailCta(
                                    'filled-light',
                                    'mt-[1.5rem]',
                                )}
                            >
                                Send Message
                                <MessageCircle className="size-4" aria-hidden />
                            </button>
                        ) : null}
                        {bookingSessionId != null ? (
                            <button
                                type="button"
                                onClick={() => setBookingOpen(true)}
                                className={detailCta(
                                    bookMeetingVariant,
                                    selected.phone || showSendMessage
                                        ? 'mt-[0.75rem]'
                                        : 'mt-[1.5rem]',
                                )}
                            >
                                Book a Meeting
                                <CalendarDays className="size-4" aria-hidden />
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
            {bookingSessionId != null ? (
                <ClientDirectorBookingModal
                    sessionId={bookingSessionId}
                    open={bookingOpen}
                    onOpenChange={setBookingOpen}
                />
            ) : null}
        </div>
    );
};
